import React, { useContext, useRef, useState, useEffect } from "react"
import { DateTime } from "luxon"
import { titleCase } from "~/utils"
import { AdminContext } from "~/contexts/AdminContext"
import { ReceiptRefundIcon, PrinterIcon, UserIcon } from "@heroicons/react/24/outline"
import Button from "~/components/ui/Button"
import Spinner from "~/components/ui/Spinner"
import { useReactToPrint } from "react-to-print"
import OrderSummary from "~/components/Admin/Orders/OrderSummary"
import RefundModalContents from "~/components/Reservations/RefundModal/RefundModalContents"
import IPayment from "~/interfaces/IPayment"
import stripeIntentUrlFor from "~/utils/admin/stripeIntentUrlFor"
import eAdminUserRole from "~/enums/eAdminUserRole"
import NewCamperModalContent from "~/components/Admin/Campers/NewCamperModalContent"
import api from "~/api2"
import ProfileCard from "../Admin/Campers/Camper/ProfileCard"
import { useOrders } from "~/hooks/useOrders"
import IOrder from "~/interfaces/IOrder"
import { CampProps } from "~/interfaces"
import usePagination from "~/hooks/usePagination"
import { useModal } from "~/contexts/ModalProvider"
import { useNotification } from "~/contexts/NotificationProvider"
import { useMe } from "~/api2/hooks/useMe"

interface OrderDetailPanelProps {
  order: IOrder
  setOrder: React.Dispatch<React.SetStateAction<IOrder>>
  camp: CampProps
  page: number
  perPage: number
}

const OrderDetailPanel: React.FC<OrderDetailPanelProps> = ({ order, setOrder, camp, page, perPage }) => {
  const { openModal, closeModal } = useModal()
  const { showNotification } = useNotification()
  const user = useMe()
  const [orderForPrint, setOrderForPrint] = useState(null)
  const [isPrintLoading, setIsPrintLoading] = useState(false)
  const {
    index: { refetch: refetchOrders },
  } = useOrders({ campId: camp?.id, isStore: true, page, perPage })
  const currentUserRole = user?.data?.roles?.[0]
  const receiptPrintRef = useRef(null)
  const usePrint = useReactToPrint({
    content: () => receiptPrintRef.current,
    onAfterPrint: () => {
      setOrderForPrint(null)
      setIsPrintLoading(false)
    },
  })

  useEffect(() => {
    if (orderForPrint?.id) {
      setIsPrintLoading(true)
      usePrint(undefined)
    }
  }, [orderForPrint])

  const handleClickRefund = (payment: IPayment, externalUrl: string | undefined): void => {
    openModal("generic", {
      children: (
        <RefundModalContents
          payment={payment}
          initialAmount={payment.amount_in_cents / 100}
          externalUrl={externalUrl}
        />
      ),
      isDynamicWidth: true,
      maxWidth: "max-w-2xl",
      onClose: () => {
        refetchOrders()
          .then((res) => {
            setOrder((prev) => res.data.orders.find((o) => o.id === order.id) || prev)
          })
          .catch((err) => {
            console.error("Failed to refetch orders:", err)
            showNotification({
              type: "error",
              title: "Error",
              description: "Failed to refetch orders",
            })
          })
        closeModal()
      },
    })
  }

  const handlePrintClick = () => {
    setOrderForPrint(order)
  }

  const onClickEditCamper = () => {
    if (order.camper_id) {
      api.Orders.updateOrderCamper(order.id, null)
        .then(() => {
          refetchOrders()
          setOrder((prev) => ({ ...prev, camper_id: null, camper: null }))
          showNotification({
            type: "success",
            title: "Guest detached",
            description: "Guest has been detached from this order",
          })
        })
        .catch((err) => {
          console.error(err)
          showNotification({
            type: "error",
            title: "Error",
            description: "Failed to detach guest",
          })
        })
    } else {
      openModal("generic", {
        children: (
          <NewCamperModalContent
            existingOrderId={order.id}
            onSuccess={(camper) => {
              setOrder((prev) => ({ ...prev, camper_id: camper.id, camper }))
              refetchOrders()
              closeModal()
            }}
          />
        ),
        onClose: () => {
          closeModal()
        },
      })
    }
  }

  if (!order) return null

  return (
    <div className="flex flex-col h-full pb-12">
      <div className="bg-white rounded-md border border-gray-200 p-4 mb-4">
        <h2 className="text-xl font-medium text-gray-900 mb-2">Order Details</h2>

        <div className="grid grid-cols-2 gap-2 mb-0.5">
          <div>
            <p className="text-sm text-gray-500">Status</p>
            <p className="text-sm font-medium pt-1">
              <span
                className={`px-2 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full 
                ${order.status === "paid" ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"}`}
              >
                {titleCase(order.status)}
              </span>
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Created on</p>
            <p className="text-sm font-medium pt-1">
              {DateTime.fromJSDate(new Date(order.created_at), {
                zone: camp.timezone,
              }).toFormat("LLL d, yyyy h:mm a")}
            </p>
          </div>
          <div className="col-span-2">
            <p className="text-sm text-gray-500">Guest</p>
            <p className="text-sm font-medium pt-1">
              <ProfileCard camper={order.camper} hideAdditionalInfo hidePaymentMethods />
            </p>
          </div>
        </div>
      </div>

      {/* Replace custom markup with OrderSummary */}
      <div className="mb-4 overflow-y-auto">
        <OrderSummary order={order} camp={camp} isIncludingLineItems={true} />
      </div>

      <div className="flex justify-end gap-x-3.5 items-center">
        <Button
          type="button"
          variant="white"
          size="large"
          shadow="small"
          className="flex-grow justify-self-end flex text-gray-600 items-center !px-4.5 max-w-[11rem]"
          onClick={handlePrintClick}
        >
          {isPrintLoading ? (
            <>
              <Spinner className="mr-2" size={4} />
              Printing...
            </>
          ) : (
            <>
              <PrinterIcon className="w-5 h-5 mr-2" />
              <span className="hidden sm:inline">Print receipt</span>
              <span className="sm:hidden">Print</span>
            </>
          )}
        </Button>

        <Button
          type="button"
          variant="white"
          size="large"
          shadow="small"
          className="flex-grow justify-self-end flex text-gray-600 items-center !px-4.5 max-w-[11rem]"
          onClick={() => onClickEditCamper()}
          dataTestId="guest-button"
        >
          <UserIcon className="w-5 h-5 -ml-1 mr-2" />
          <span className="hidden sm:inline">{order?.camper_id ? "Detach Guest" : "Attach Guest"}</span>
          <span className="sm:hidden">{order?.camper_id ? "Detach" : "Attach"}</span>
        </Button>

        {![eAdminUserRole.UserNoRefund, eAdminUserRole.ViewOnly].includes(currentUserRole) &&
          order.payments?.length === 1 &&
          (order.payments[0].refunds?.length > 0
            ? order.payments[0].refunds[0]?.amount_in_cents < order.payments[0].amount_in_cents
            : true) && (
            <Button
              type="button"
              variant="white"
              size="large"
              shadow="small"
              className="flex-grow justify-self-end flex text-gray-600 !px-4.5 max-w-[11rem]"
              onClick={() =>
                handleClickRefund(
                  order.payments[0],
                  order.payments[0].stripe_charge_id &&
                    stripeIntentUrlFor(camp.stripe_account_id, order.payments[0].stripe_charge_id),
                )
              }
            >
              <ReceiptRefundIcon className="w-5 h-5 -ml-1 mr-2" />
              Refund
            </Button>
          )}
      </div>

      <div className="w-full mt-3 border-t border-gray-200 hidden">
        <div className="m-8" ref={receiptPrintRef}>
          <OrderSummary order={orderForPrint} camp={camp} isPreview isIncludingLineItems={true} />
        </div>
      </div>
    </div>
  )
}

export default OrderDetailPanel
